import React from 'react';
import { Box, Button, Grid, Link, Typography, Stack } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { createStyles, makeStyles } from '@mui/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { NoData } from 'components/text/NoData';
import { useSpSidebarHandlers } from '../SpSidebarProvider';
import { SidebarProp } from '../../../../../sidebar/SidebarProp';
import { formatPhoneInternationalPretty } from '../../../../../../utils/phone';
import { GoogleMapLink } from '../../../../../map/utils/googleMap';
import { formatAddress2 } from '../../../../../../utils/places';
import { colors } from '../../../../../theme';
import { EmailMessageModal } from './EmailMessageModal';

const useStyles = makeStyles((theme) => createStyles({
  icon: {
    marginLeft: theme.spacing(1),
  },
  checkIcon: {
    color: colors.green,
    marginRight: theme.spacing(1),
  },
  contactInfo: {
    color: colors.darkGrey,
  },
}));

export const ContactsSubPage = ({ organizationId, organization }: any) => {
  const classes = useStyles();
  const { modifyExtra } = useSpSidebarHandlers();
  const {
    first_name,
    last_name,
    phone_number,
    work_email,
    login_email,
  } = organization.createdBy ?? {};
  const email = work_email ?? login_email;

  const [openEmailMessageModal, setOpenEmailMessageModal] = React.useState(false);
  const [sentSuccessfully, setSentSuccessfully] = React.useState<boolean>(false);

  return (
    <>
      <Box key="contacts" sx={{
        m: 2,
      }}>
        <Button
          size="large"
          variant="text"
          color="primary"
          startIcon={<ArrowBack />}
          onClick={() => modifyExtra((p) => ({ ...p, page: 'main' }))}
        >
          Contact Information
        </Button>
        <Box
          sx={{
            ml: 1,
            mt: 2,
          }}>
          <SidebarProp
            title="Organization Contact"
            value={(
              <Typography variant="body1" component="div" className={classes.contactInfo}>
                {organization.phone_number
                  ? formatPhoneInternationalPretty(organization.phone_number)
                  : null}
                <br />
                <GoogleMapLink
                  latlongOrPlaceId={
                    organization.address?.latlong || organization.address?.google_place_id
                  }
                >
                  {organization.address ? formatAddress2(
                    organization.address.address_line_1,
                    organization.address.address_line_2,
                    organization.address.city,
                    organization.address.state_code,
                    organization.address.postal_code,
                  ) : 'No Address'}
                </GoogleMapLink>
                {organization.website_url && (
                  <>
                    <br />
                    <Link href={organization.website_url} component="a" target="_blank">
                      {organization.website_url}
                    </Link>
                  </>
                )}
              </Typography>
            )}
          />
          <SidebarProp
            title="Owner Contact"
            value={(
              <Typography variant="body1" component="div" className={classes.contactInfo}>
                <Stack>
                  <Box sx={{
                    mb: 0.5,
                  }}>
                    {first_name}
                    {' '}
                    {last_name}
                  </Box>
                  <Box>
                    {phone_number ? formatPhoneInternationalPretty(phone_number) : null}
                  </Box>
                  <Grid
                    container
                    direction="row"
                    spacing={1}
                    sx={{
                      alignItems: 'center',
                      mt: -0.5,
                    }}>
                    <Grid>
                      {email ?? <NoData>No Email</NoData>}
                    </Grid>
                    <Grid>
                      <Button
                        variant="text"
                        color="primary"
                        onClick={() => setOpenEmailMessageModal(true)}
                        startIcon={<FontAwesomeIcon icon={['fas', 'envelope']} className={classes.icon} />}
                        endIcon={sentSuccessfully
                          ? <FontAwesomeIcon icon={['fas', 'check']} className={classes.checkIcon} />
                          : null}
                      >
                        Send Email
                      </Button>
                    </Grid>
                  </Grid>
                </Stack>
              </Typography>
            )}
          />
        </Box>
      </Box>
      <EmailMessageModal
        organization={{ organization_id: organizationId, name: organization.name }}
        open={openEmailMessageModal}
        onClose={(success) => {
          setOpenEmailMessageModal(false);
          if (success) {
            setSentSuccessfully(true);
          }
        }}
      />
    </>
  );
};
